/**
 * Everything a shopper has set aside: favourited products, favourited vendors, bookmarked
 * services, and the sizes they asked to be told about.
 *
 * One screen with tabs rather than four routes. All four are the same gesture from the
 * shopper's point of view — "keep this for me" — and they are all short lists, so splitting
 * them across the nav would mean four half-empty screens and four places to look.
 *
 * The rule every tab follows: a saved row is NEVER silently dropped. A product that sold out,
 * a listing the vendor paused, a vendor who was suspended, a product that was deleted — each
 * one stays in the list and says what happened. Removing it for them is how you get "where did
 * my saved item go", and a greyed row is recoverable while a missing one is not.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing, Heart, Loader2, Store } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { AuthImage } from '@/components/auth-image';
import { ServiceThumb } from '@/components/service-thumb';
import { cn } from '@/lib/utils';
import { FavouriteProductButton } from '@/components/favourite-product-button';
import {
  use_bookmarked_services,
  use_favourite_products,
  use_favourite_vendors,
  use_toggle_favourite_vendor,
  use_toggle_bookmark_service,
} from '@/features/favourite/hooks';
import { use_stock_alerts, use_unwatch_variant } from '@/features/stock/hooks';
import type { FavouriteProduct } from '@/features/favourite/api';

type Tab = 'products' | 'vendors' | 'services' | 'watching';

/** Customer-facing wording for why something cannot be bought. */
const REASON_COPY: Record<string, string> = {
  OUT_OF_STOCK: 'Out of stock',
  LISTING_PAUSED: 'Not on sale right now',
  NOT_APPROVED: 'Awaiting review',
  VENDOR_UNAVAILABLE: 'Seller unavailable',
  NO_LONGER_SOLD: 'No longer sold',
};

function ProductRow({ row }: { row: FavouriteProduct }) {
  const p = row.product;
  const vertical = row.vertical.toLowerCase() as 'food' | 'pharmacy';
  const thumb = p?.primary_image?.thumb_path ?? null;

  /*
   * A deleted product has no card left to render, so the row degrades to the bare fact that
   * it is gone plus a working heart to clear it. Everything else still renders in full.
   */
  const body = (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-card bg-secondary/30">
        {thumb ? (
          <AuthImage path={thumb} alt={p?.name ?? 'Product'} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-2xl">🐾</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{p?.name ?? 'This product'}</p>
        {p?.brand && <p className="truncate text-xs text-muted-foreground">{p.brand}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {p?.price_from_inr && (
            <span className="text-sm font-bold tabular-nums">₹{p.price_from_inr}</span>
          )}
          {p?.pack_size && <span className="text-xs text-muted-foreground">{p.pack_size}</span>}
          {!row.is_available && (
            <StatusPill tone="destructive" data-testid={`saved-product-unavailable-${row.product_id}`}>
              {REASON_COPY[row.unavailable_reason ?? ''] ?? 'Unavailable'}
            </StatusPill>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <li
      className="flex items-center gap-3 py-3"
      data-testid={`saved-product-${row.product_id}`}
      data-available={row.is_available}
    >
      {p ? (
        <Link to={`/app/shop/p/${p.id}?vertical=${vertical}`} className="flex min-w-0 flex-1 items-center">
          {body}
        </Link>
      ) : (
        body
      )}
      <FavouriteProductButton
        vertical={vertical}
        product_id={row.product_id}
        product_name={p?.name}
        size="sm"
      />
    </li>
  );
}

export function SavedPage() {
  const [tab, set_tab] = useState<Tab>('products');

  const products_q = use_favourite_products();
  const vendors_q = use_favourite_vendors();
  const services_q = use_bookmarked_services();
  const alerts_q = use_stock_alerts();

  const toggle_vendor = use_toggle_favourite_vendor();
  const toggle_service = use_toggle_bookmark_service();
  const unwatch = use_unwatch_variant();

  const tabs: Array<{ key: Tab; label: string; count: number | undefined }> = [
    { key: 'products', label: 'Products', count: products_q.data?.length },
    { key: 'vendors', label: 'Providers', count: vendors_q.data?.length },
    { key: 'services', label: 'Services', count: services_q.data?.length },
    { key: 'watching', label: 'Notify me', count: alerts_q.data?.length },
  ];

  const active_q =
    tab === 'products' ? products_q : tab === 'vendors' ? vendors_q : tab === 'services' ? services_q : alerts_q;

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="saved-page">
      <SectionHeader title="Wishlist" description="Products, providers and services you kept — plus what you're waiting for." />

      <div className="flex flex-wrap gap-2" data-testid="saved-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => set_tab(t.key)}
            data-testid={`saved-tab-${t.key}`}
            data-active={tab === t.key}
            className={cn(
              'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-colors',
              tab === t.key
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-brand-outline-variant/25 text-muted-foreground hover:border-primary/40',
            )}
          >
            {t.label}
            {t.count !== undefined && <span className="ml-1.5 tabular-nums opacity-70">{t.count}</span>}
          </button>
        ))}
      </div>

      {active_q.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : active_q.isError ? (
        <ErrorState title="Couldn't load your saved items" description="Pull to refresh, or try again in a moment." />
      ) : (
        <Card className="p-4">
          {tab === 'products' &&
            ((products_q.data ?? []).length === 0 ? (
              <EmptyState
                title="No saved products yet"
                description="Tap the heart on anything in the Shop and it'll wait for you here."
                data-testid="saved-products-empty"
              />
            ) : (
              <ul className="divide-y divide-border" data-testid="saved-products">
                {(products_q.data ?? []).map((row) => (
                  <ProductRow key={`${row.vertical}:${row.product_id}`} row={row} />
                ))}
              </ul>
            ))}

          {tab === 'vendors' &&
            ((vendors_q.data ?? []).length === 0 ? (
              <EmptyState title="No saved providers yet" description="Tap the heart on a provider's page." data-testid="saved-vendors-empty" />
            ) : (
              <ul className="divide-y divide-border" data-testid="saved-vendors">
                {(vendors_q.data ?? []).map((f) => (
                  <li key={f.id} className="flex items-center gap-3 py-3" data-testid={`saved-vendor-${f.vendor.id}`}>
                    <Link to={`/app/discover/${f.vendor.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Store className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{f.vendor.business_name ?? 'Provider'}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[f.vendor.city, `${f.vendor.review_count} reviews`].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggle_vendor.mutate({ vendor_id: f.vendor.id, favourited: true })}
                      aria-label={`Remove ${f.vendor.business_name ?? 'provider'} from favourites`}
                      className="shrink-0 rounded-full border-2 border-primary/40 p-2 text-primary active:scale-90"
                      data-testid={`saved-vendor-remove-${f.vendor.id}`}
                    >
                      <Heart className="h-4 w-4 fill-current" />
                    </button>
                  </li>
                ))}
              </ul>
            ))}

          {tab === 'services' &&
            ((services_q.data ?? []).length === 0 ? (
              <EmptyState title="No bookmarked services yet" description="Bookmark a service from a provider's page." data-testid="saved-services-empty" />
            ) : (
              <ul className="divide-y divide-border" data-testid="saved-services">
                {(services_q.data ?? []).map((b) => (
                  <li key={b.id} className="flex items-center gap-3 py-3" data-testid={`saved-service-${b.service.id}`}>
                    {/*
                      Was a generic wrench for every saved service, whatever it was. Shows the
                      service's own photo now, falling back to its module's icon rather than to
                      one shape that fits nothing.
                    */}
                    <ServiceThumb
                      module_type={b.service.module_type}
                      thumb_path={b.service.images?.[0]?.thumb_path}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{b.service.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[b.service.vendor?.business_name, `₹${b.service.price_inr}`].filter(Boolean).join(' · ')}
                      </p>
                      {!b.service.is_active && (
                        <StatusPill tone="destructive" data-testid={`saved-service-inactive-${b.service.id}`}>
                          Not bookable
                        </StatusPill>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggle_service.mutate({ service_id: b.service.id, bookmarked: true })}
                      aria-label={`Remove ${b.service.name} bookmark`}
                      className="shrink-0 rounded-full border-2 border-primary/40 p-2 text-primary active:scale-90"
                      data-testid={`saved-service-remove-${b.service.id}`}
                    >
                      <Heart className="h-4 w-4 fill-current" />
                    </button>
                  </li>
                ))}
              </ul>
            ))}

          {tab === 'watching' &&
            ((alerts_q.data ?? []).length === 0 ? (
              <EmptyState
                title="You're not waiting on anything"
                description="When something's out of stock, tap “Notify me” and it'll show up here."
                data-testid="saved-watching-empty"
              />
            ) : (
              <ul className="divide-y divide-border" data-testid="saved-watching">
                {(alerts_q.data ?? []).map((a) => {
                  const p = a.product;
                  const thumb = p?.primary_image?.thumb_path ?? null;
                  return (
                    <li
                      key={a.variant_id}
                      className="flex items-center gap-3 py-3"
                      data-testid={`saved-watch-${a.variant_id}`}
                      data-notified={a.notified_at !== null}
                    >
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-card bg-secondary/30">
                        {thumb ? (
                          <AuthImage path={thumb} alt={p?.name ?? 'Product'} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-xl">🐾</div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{p?.name ?? a.label}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {a.variant_label ?? '—'}
                          {p?.price_from_inr ? ` · ₹${p.price_from_inr}` : ''}
                        </p>
                        {/*
                          Three states worth distinguishing: it's back (act now), we already
                          told you, or still waiting. A single "waiting" pill would hide the
                          one case the shopper opened this screen for.
                        */}
                        {a.is_purchasable ? (
                          <StatusPill tone="success" data-testid={`saved-watch-back-${a.variant_id}`}>
                            Back in stock
                          </StatusPill>
                        ) : a.notified_at ? (
                          <StatusPill tone="info">We told you</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">
                            {REASON_COPY[a.unavailable_reason ?? ''] ?? 'Waiting'}
                          </StatusPill>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {a.is_purchasable && p && (
                          <Link
                            to={`/app/shop/p/${p.id}?vertical=${a.vertical.toLowerCase()}`}
                            className="rounded-button bg-primary px-3 py-1.5 text-sm font-semibold text-white active:scale-95"
                            data-testid={`saved-watch-buy-${a.variant_id}`}
                          >
                            Buy
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => unwatch.mutate(a.variant_id)}
                          aria-label={`Stop waiting for ${a.label}`}
                          title="Stop waiting"
                          className="rounded-full border-2 border-primary/40 p-2 text-primary active:scale-90"
                          data-testid={`saved-watch-remove-${a.variant_id}`}
                        >
                          {a.notified_at ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ))}
        </Card>
      )}
    </div>
  );
}
